import { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../services/axiosInterceptor';

export interface SubjectOption {
  label: string;
  value: string;
  id: string;
  subjectName: string;
  classId: string;
}

// ✅ Updated to match Go backend response
interface SubjectResponse {
  subject_id: string;
  subject_name: string;
  class_id: string;
  class_name?: string;
  subject_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface SubjectsApiResponse {
  status: 'success' | 'error';
  subjects: SubjectResponse[];
  class_name?: string;
  message?: string;
}

export const useSubjectsByClass = (classId?: string | null, includePlaceholder: boolean = false) => {
  const [subjectOptions, setSubjectOptions] = useState<SubjectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!classId) {
        setSubjectOptions(
          includePlaceholder
            ? [{ label: 'Select a Subject', value: '', id: '', subjectName: '', classId: '' }]
            : []
        );
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // ✅ Fixed: Use correct endpoint /subjects_by_class
        const response = await apiClient.get<SubjectsApiResponse>(
          `/subjects_by_class?class_id=${classId}`
        );

        console.log(response.data)

        if (response.data.status === 'success') {
          const subjects = response.data.subjects || [];
          
          if (subjects.length === 0) {
            setSubjectOptions(
              includePlaceholder
                ? [{ label: 'No subjects available', value: '', id: '', subjectName: '', classId: '' }]
                : []
            );
            setError('No subjects available for this class');
            toast.current?.show({
              severity: 'warn',
              summary: 'Warning',
              detail: 'No subjects available for this class',
              life: 3000,
            });
          } else {
            const options: SubjectOption[] = subjects
              .sort((a, b) => a.subject_name.localeCompare(b.subject_name))
              .map((sub) => ({
                label: sub.subject_name,
                value: sub.subject_id,
                id: sub.subject_id,
                subjectName: sub.subject_name,
                classId: sub.class_id,
              }));

            setSubjectOptions(
              includePlaceholder
                ? [
                    { label: 'Select a Subject', value: '', id: '', subjectName: '', classId: '' },
                    ...options,
                  ]
                : options
            );
          }
        } else {
          setError(response.data.message || 'Failed to load subjects.');
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response.data.message || 'Failed to load subjects.',
            life: 3000,
          });
        }
      } catch (err: any) {
        console.error('useSubjectsByClass error:', err);
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'An error occurred while fetching subjects.';
        setError(message);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: message,
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [classId, includePlaceholder]);

  return {
    subjectOptions,
    loading,
    error,
    toast,
  };
};